import { Link, NavLink, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { toast } from "@/components/ui/use-toast";
import Icon from "@/components/Icon";

type ProfileDoc = { username?: string | null };

function useUsername(): string | null {
  const [fbUser, setFbUser] = useState<User | null>(auth.currentUser);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFbUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!fbUser) {
      setUsername(null);
      return;
    }
    const ref = doc(db, "users", fbUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const data = (snap.data() ?? {}) as ProfileDoc;
      setUsername((data.username ?? "").trim() || null);
    });
    return () => unsub();
  }, [fbUser?.uid]);

  return username;
}

function isActivePath(pathname: string, base: string): boolean {
  if (base === "/") return pathname === "/";
  return pathname === base || pathname.startsWith(`${base}/`);
}

export default function BottomNav(): JSX.Element {
  const location = useLocation();
  const username = useUsername();

  const origin = useMemo<string>(() => {
    // Always share the production domain if we detect it, else current origin
    const o = window.location.origin;
    return o.includes("spellgather.com") ? "https://www.spellgather.com" : o;
  }, []);

  const shareUrl = useMemo<string>(() => {
    // Prefer /:username if we have one, else fall back to profile root
    return username ? `${origin}/${username}` : `${origin}/dashboard`;
  }, [origin, username]);

  const baseItem = "flex-1 grid place-items-center py-2 select-none";
  const ICON_SIZE = 34; // doubled from 22

  return (
    <nav className="fixed bottom-0 inset-x-0 h-20 bg-gray-900 backdrop-blur border-t border-gray-800 flex items-center justify-around">
      {/* Home */}
      <Link
        to="/dashboard"
        data-tour="nav-dashboard"
        className={`${baseItem} ${isActivePath(location.pathname, "/dashboard") ? "bg-white/10" : "hover:bg-white/5"}`}
        aria-label="Dashboard"
      >
        <Icon name="home" size={ICON_SIZE} className="text-white/80" />
        <span className="text-[10px] leading-none text-white/80">Home</span>
      </Link>

      {/* Community Feed */}
      <Link
        to="/community"
        data-tour="community"
        className={`${baseItem} ${isActivePath(location.pathname, "/community") ? "bg-white/10" : "hover:bg-white/5"}`}
        aria-label="Community Feed"
      >
        <Icon name="social" size={ICON_SIZE} className="text-white/80" />
        <span className="text-[10px] leading-none text-white/80">Community</span>
      </Link>

      {/* Trades */}
      <Link
        to="/trades"
        className={`${baseItem} ${isActivePath(location.pathname, "/trades") ? "bg-white/10" : "hover:bg-white/5"}`}
        aria-label="Trades"
      >
        <Icon name="trades" size={ICON_SIZE} className="text-white/80" />
        <span className="text-[10px] leading-none text-white/80">Trades</span>
      </Link>

      {/* My Collection */}
      <Link
        to="/collection"
        data-tour="collection"
        className={`${baseItem} ${isActivePath(location.pathname, "/collection") ? "bg-white/10" : "hover:bg-white/5"}`}
        aria-label="My Collection"
      >
        <Icon name="heart" size={ICON_SIZE} className="text-white/80" />
        <span className="text-[10px] leading-none text-white/80">My Collection</span>
      </Link>
      {/* Search */}
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("app:openQuickSearch"));
        }}
        className={`${baseItem} hover:bg-white/5`}
        aria-label="Quick Search"
      >
        <Icon name="search" size={ICON_SIZE} className="text-white/80" />
        <span className="text-[10px] leading-none text-white/80">Search</span>
      </button>
    </nav>
  );
}
